import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const MatchedBettingCalculator = () => {
  const [betData, setBetData] = useState({
    odds: '',
    stake: '',
    freeBet: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBetData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateProfit = () => {
    const { odds, stake, freeBet } = betData;

    // Convert inputs to numbers
    const oddsNum = parseFloat(odds) || 0;
    const stakeNum = parseFloat(stake) || 0;
    const freeBetNum = parseFloat(freeBet) || 0;

    if (oddsNum === 0 || stakeNum === 0 || freeBetNum === 0) {
      setError('Please enter valid odds, stake, and free bet amount');
      return;
    }

    // Calculate potential profit
    const potentialProfit = (oddsNum * freeBetNum) - stakeNum;

    const newResults = {
      potentialProfit
    };

    setResults(newResults);

    // Update chart data
    setChartData({
      labels: ['Stake', 'Free Bet', 'Potential Profit'],
      datasets: [
        {
          label: 'Betting Breakdown',
          data: [stakeNum, freeBetNum, potentialProfit],
          backgroundColor: ['rgba(54, 162, 235, 0.5)', 'rgba(255, 99, 132, 0.5)', 'rgba(75, 192, 192, 0.5)'],
          borderColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)', 'rgba(75, 192, 192, 1)'],
          borderWidth: 1
        }
      ]
    });
  };

  const exampleData = {
    odds: 2.5,
    stake: 10,
    freeBet: 20,
    potentialProfit: 30
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Matched Betting Calculator</title>
        <link rel="canonical" href="http://example.com/matched-betting-calculator" />
        <meta property="og:title" content="Matched Betting Calculator" />
        <meta property="og:description" content="Calculate the potential profit from matched betting based on odds, stake, and free bet amount." />
        <meta property="og:image" content="http://example.com/image.jpg" />
        <meta property="og:url" content="http://example.com/matched-betting-calculator" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🎲</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Matched Betting Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Calculate the potential profit from matched betting based on odds, stake, and free bet amount</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          This calculator helps you determine the potential profit from matched betting based on the odds, stake, and free bet amount. Ensure you enter accurate data for the best results.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="odds">
              Odds
            </label>
            <input
              type="number"
              id="odds"
              name="odds"
              value={betData.odds}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="stake">
              Stake
            </label>
            <input
              type="number"
              id="stake"
              name="stake"
              value={betData.stake}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="freeBet">
              Free Bet Amount
            </label>
            <input
              type="number"
              id="freeBet"
              name="freeBet"
              value={betData.freeBet}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <button
            onClick={calculateProfit}
            className="w-full bg-green-600 text-white py-3 px-4 rounded-md text-sm font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Calculate Profit
          </button>

          {error && (
            <div className="text-red-500 text-center mt-4">
              {error}
            </div>
          )}
        </div>

        {results && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Results</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-semibold mb-4">Potential Profit</h3>
                <p className="text-xl font-medium">{results.potentialProfit.toFixed(2)}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
              <div>
                <h3 className="text-xl font-semibold mb-4">Betting Breakdown Visualization</h3>
                <div className="h-96">
                  <Bar
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Amount'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Component'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Betting Breakdown'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Example Calculation <span className="ml-2 text-2xl">📝</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Here's an example calculation using sample betting data:
          </p>
          
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold mb-4">Sample Betting Data:</h4>
            <ul className="list-disc pl-8 space-y-2">
              <li>Odds: {exampleData.odds}</li>
              <li>Stake: ${exampleData.stake}</li>
              <li>Free Bet Amount: ${exampleData.freeBet}</li>
            </ul>
            
            <h4 className="font-semibold mt-6 mb-4">Calculations:</h4>
            <ol className="list-decimal pl-8 space-y-4">
              <li>
                <p>Potential Profit Calculation:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>Potential Profit = (Odds × Free Bet Amount) - Stake = ({exampleData.odds} × {exampleData.freeBet}) - {exampleData.stake} = {exampleData.potentialProfit}</li>
                </ul>
              </li>
            </ol>
            
            <p className="mt-6 font-semibold">Interpretation:</p>
            <p>In this example, the potential profit from matched betting is ${exampleData.potentialProfit}.</p>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Matched Betting Calculator:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Betting Data</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Input the odds</li>
                <li>Input the stake amount</li>
                <li>Input the free bet amount</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Click Calculate</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>The calculator will determine the potential profit from matched betting</li>
                <li>A visual representation of the betting breakdown will be displayed</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Interpret Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Review the potential profit from matched betting</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Pro Tips</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Use accurate odds, stake, and free bet amount for the best results</li>
                <li>Ensure the odds are correctly entered</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding the Results <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            The Matched Betting Calculator provides the potential profit from matched betting based on the odds, stake, and free bet amount.
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Potential Profit:</span> The calculated potential profit from matched betting.
            </li>
          </ul>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Additional Tips <span className="ml-2 text-2xl">💡</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Data Quality:</span> Enter accurate odds, stake, and free bet amount to ensure the calculations are correct.
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Consult Betting Sites:</span> For more detailed insights, consider consulting official betting sites or forums.
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Adjustments:</span> Adjustments may be needed for special cases or promotions.
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Limitations:</span> The calculator assumes standard conditions. Adjustments may be needed for special cases.
            </li>
          </ul>
          <p className="mt-6 italic text-gray-500">
            Note: Always ensure the betting data meets your requirements and standards.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MatchedBettingCalculator;